/**
 * 按画在地图上的范围判区。
 *
 * ★ 关键词只看地址文字,分不清「地址里带着偏远区的词、其实离得很近」;
 *   坐标落在哪个圈里是实打实的位置。所以订单有坐标、又落进了画好的圈,就以圈为准。
 *
 * ★ 圈互相重叠时取**面积最小**的:小圈画在大圈里,通常就是「这一片要单独算」。
 *
 * 坐标一律 [lng, lat](GeoJSON 的顺序)。多边形是 GeoJSON MultiPolygon 的 coordinates:
 * 多边形[] → 环[](第一个是外圈,后面的是洞) → 点[]。
 */
export type LngLat = [number, number];
export type Ring = LngLat[];
export type MultiPolygon = Ring[][];

/** 射线法:点在不在这个环里(不含边上的精确判断,够用) */
export function pointInRing([x, y]: LngLat, ring: Ring): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** 点在不在这个范围里:落在某个多边形的外圈里、又不在它的洞里 */
export function pointInBoundary(p: LngLat, mp: MultiPolygon): boolean {
  return mp.some(([outer, ...holes]) => !!outer && pointInRing(p, outer) && !holes.some((h) => pointInRing(p, h)));
}

/** 面积(经纬度平方,只用来比大小,不是平方米) */
export function boundaryArea(mp: MultiPolygon): number {
  const ringArea = (r: Ring) => {
    let a = 0;
    for (let i = 0, j = r.length - 1; i < r.length; j = i++) a += (r[j]![0] + r[i]![0]) * (r[j]![1] - r[i]![1]);
    return Math.abs(a / 2);
  };
  return mp.reduce((sum, [outer, ...holes]) => sum + (outer ? ringArea(outer) : 0) - holes.reduce((h, r) => h + ringArea(r), 0), 0);
}

/** 坐标落在哪个区的圈里;好几个圈都包着它时取面积最小的。没有就 null */
export function matchBoundary(
  p: LngLat,
  zones: ReadonlyArray<{ zoneId: string; boundary: MultiPolygon }>,
): string | null {
  let best: { zoneId: string; area: number } | null = null;
  for (const z of zones) {
    if (!pointInBoundary(p, z.boundary)) continue;
    const area = boundaryArea(z.boundary);
    if (!best || area < best.area) best = { zoneId: z.zoneId, area };
  }
  return best?.zoneId ?? null;
}

/**
 * 画出来的点 → 合法的范围。
 * 至少 3 个不同的点;首尾自动闭合(GeoJSON 要求环的最后一点等于第一点)。
 * 不合法返回原因,而不是硬存一个画不出来的形状。
 */
export function ringFromPoints(points: readonly LngLat[]): { ring: Ring } | { error: string } {
  const pts = points.filter(
    ([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180,
  );
  const distinct = new Set(pts.map(([a, b]) => `${a.toFixed(7)},${b.toFixed(7)}`));
  if (distinct.size < 3) return { error: '至少要点 3 个不同的位置才围得成一个范围' };
  const ring: Ring = [...pts];
  const [f, l] = [ring[0]!, ring[ring.length - 1]!];
  if (f[0] !== l[0] || f[1] !== l[1]) ring.push([f[0], f[1]]);
  return { ring };
}

/*
 * ── 画范围用的几何小工具 ─────────────────────────────────
 *
 * ★ 用户原话:「这个画圈的方式很难用啊,很容易就出现莫名其妙的形状」。
 *   根子是新点一律接在最后:一旦不是严格顺着边界点(回头补一个拐角),
 *   最后一点和第一点之间那条边就会横穿整个圈,画成一个「8」字。
 */

/** 近似平面距离:经度按纬度缩一下,不然高纬度地方东西方向被放大 */
function dist(a: LngLat, b: LngLat): number {
  const k = Math.cos((((a[1] + b[1]) / 2) * Math.PI) / 180);
  return Math.hypot((a[0] - b[0]) * k, a[1] - b[1]);
}

/**
 * 新点该插在第几位(插入后的下标)。
 *
 * ★ 还不到 3 个点时接在最后(那时候还在描边);够 3 个点以后,
 *   插到**让周长增加最少**的那条边上(含最后连回第一点的那条)——
 *   顺着边界往下点时,这条边就是「最后一点 → 第一点」,和原来一样;
 *   回头在某条边附近补一个点时,它会落进那条边,而不是去连最后一个点。
 */
export function insertIndex(ring: readonly LngLat[], p: LngLat): number {
  if (ring.length < 3) return ring.length;
  let best = ring.length;
  let bestCost = Infinity;
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!;
    const b = ring[(i + 1) % ring.length]!;
    const cost = dist(a, p) + dist(p, b) - dist(a, b);
    if (cost < bestCost) {
      bestCost = cost;
      best = i + 1;
    }
  }
  return best;
}

function orient(a: LngLat, b: LngLat, c: LngLat): number {
  const v = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
  return v > 0 ? 1 : v < 0 ? -1 : 0;
}

function onSegment(a: LngLat, b: LngLat, p: LngLat): boolean {
  return (
    Math.min(a[0], b[0]) <= p[0] && p[0] <= Math.max(a[0], b[0]) && Math.min(a[1], b[1]) <= p[1] && p[1] <= Math.max(a[1], b[1])
  );
}

function segmentsTouch(a1: LngLat, a2: LngLat, b1: LngLat, b2: LngLat): boolean {
  const o1 = orient(a1, a2, b1);
  const o2 = orient(a1, a2, b2);
  const o3 = orient(b1, b2, a1);
  const o4 = orient(b1, b2, a2);
  if (o1 !== o2 && o3 !== o4) return true;
  return (
    (o1 === 0 && onSegment(a1, a2, b1)) ||
    (o2 === 0 && onSegment(a1, a2, b2)) ||
    (o3 === 0 && onSegment(b1, b2, a1)) ||
    (o4 === 0 && onSegment(b1, b2, a2))
  );
}

/**
 * 画出来的圈(首尾**不**重复,自动闭合)有没有边交叉。
 * 交叉的圈存进去服务端也会拒(ST_IsValid),在画的时候就标出来,别等点保存才知道。
 */
export function ringSelfIntersects(ring: readonly LngLat[]): boolean {
  const n = ring.length;
  if (n < 4) return false;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      // 相邻的两条边本来就共用一个点
      if (j === i + 1 || (i === 0 && j === n - 1)) continue;
      if (segmentsTouch(ring[i]!, ring[(i + 1) % n]!, ring[j]!, ring[(j + 1) % n]!)) return true;
    }
  }
  return false;
}

/**
 * 以一个点为圆心、半径若干米的圆(用 n 个点近似,首尾不重复)。
 *
 * ★ 「邮编里单独算价的那一小片」多数就是「某个地方周围一圈」——
 *   点一下圆心、选个半径,比沿着边界点十几下省事得多,也不会画歪。
 *   生成之后每个点还能拖。
 */
export function circleRing(center: LngLat, radiusMeter: number, n = 16): LngLat[] {
  const [lng, lat] = center;
  const dLat = radiusMeter / 111_320;
  const dLng = radiusMeter / (111_320 * Math.cos((lat * Math.PI) / 180));
  return Array.from({ length: n }, (_, i) => {
    const t = (2 * Math.PI * i) / n;
    return [lng + dLng * Math.cos(t), lat + dLat * Math.sin(t)] as LngLat;
  });
}

/**
 * 手绘的一笔精简成多边形的点(Douglas-Peucker)。
 *
 * ★ 范围往往不是圆,而是沿着路、河、海岸线拐来拐去的一片 ——
 *   最顺手的画法是按住鼠标沿边界描一圈。可描一圈会采到几百个点,
 *   每个都是一个可拖的圆点,根本没法改;所以只留下拐弯处的点。
 *
 * @param tolerance 偏离直线多少以内的点可以丢(和输入同一个单位 —— 管理端传的是屏幕像素)
 * 首尾不重复;起点和终点挨得很近(描完一圈回到起点)时去掉终点。
 */
export function simplifyPath(path: readonly LngLat[], tolerance: number): LngLat[] {
  const out = douglasPeucker(path, tolerance);
  const first = out[0];
  const last = out[out.length - 1];
  if (first && last && out.length > 3 && Math.hypot(first[0] - last[0], first[1] - last[1]) <= tolerance * 3) out.pop();
  return out;
}

function douglasPeucker(path: readonly LngLat[], tolerance: number): LngLat[] {
  if (path.length <= 2) return [...path];
  const keep = new Array<boolean>(path.length).fill(false);
  keep[0] = keep[path.length - 1] = true;
  const stack: Array<[number, number]> = [[0, path.length - 1]];
  while (stack.length) {
    const [s, e] = stack.pop()!;
    const a = path[s]!;
    const b = path[e]!;
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
    let far = -1;
    let farD = tolerance;
    for (let i = s + 1; i < e; i++) {
      const p = path[i]!;
      const d =
        len === 0
          ? Math.hypot(p[0] - a[0], p[1] - a[1])
          : Math.abs((b[0] - a[0]) * (a[1] - p[1]) - (a[0] - p[0]) * (b[1] - a[1])) / len;
      if (d > farD) {
        farD = d;
        far = i;
      }
    }
    if (far >= 0) {
      keep[far] = true;
      stack.push([s, far], [far, e]);
    }
  }
  return path.filter((_, i) => keep[i]);
}

const px = (a: LngLat, b: LngLat) => Math.hypot(a[0] - b[0], a[1] - b[1]);

/** 点到线段的距离(平面,屏幕像素);along = 垂足离 a 多远 */
function nearOnSegment(p: LngLat, a: LngLat, b: LngLat): { d: number; along: number } {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const l2 = dx * dx + dy * dy;
  const t = l2 === 0 ? 0 : Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2));
  return { d: Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy)), along: t * Math.sqrt(l2) };
}

/**
 * 手绘分好几笔:把新的一笔接到已经描好的那段上。
 *
 * ★ 想画准就得放大,放大了一屏只装得下一段边界 ——
 *   描一段、挪地图、再接着描,反复几次才绕完一圈。所以一笔松手不能就当画完了。
 *
 * ★ 接头处人手不会刚好对上:新的一笔从上一笔末尾往回一点的地方起笔,
 *   直接拼起来会在接头处折出一根刺。所以起笔落在已描那段的尾巴附近(snap 以内)时,
 *   从起笔那里把旧的剪断再接;反着描(从尾巴往回描、从开头往外描)也认。
 *
 * ★ 最后一笔回到起点附近(snap 以内)就算绕完一圈:closed = true,多出来的那截剪掉。
 *
 * 全部用屏幕像素(调用方按当前视角把经纬度换成像素),tolerance 是精简用的像素数。
 * @returns ignored = 这一笔太短(点一下),不算
 */
export function joinStroke(
  trace: readonly LngLat[],
  stroke: readonly LngLat[],
  { tolerance, snap }: { tolerance: number; snap: number },
): { path: LngLat[]; closed: boolean; ignored: boolean } {
  let s = douglasPeucker(stroke, tolerance);
  if (s.length < 2 || (s.length < 3 && px(s[0]!, s[s.length - 1]!) < tolerance)) {
    return { path: [...trace], closed: false, ignored: true };
  }
  if (trace.length === 0) {
    const closed = s.length >= 3 && px(s[0]!, s[s.length - 1]!) <= snap;
    if (closed) s.pop(); // 最后连回第一点那条边是自动补的
    return { path: s, closed, ignored: false };
  }

  let t = [...trace];
  const ss = s[0]!;
  const se = s[s.length - 1]!;
  if (px(ss, t[t.length - 1]!) > snap && t.length >= 2) {
    // 起笔不在尾巴上:看是不是反着描的
    if (px(se, t[t.length - 1]!) <= snap) s = s.reverse();
    else if (px(ss, t[0]!) <= snap) t = t.reverse();
    else if (px(se, t[0]!) <= snap) {
      t = t.reverse();
      s = s.reverse();
    }
  }

  // 接头:起笔落在尾巴附近的哪条边上,就从那里剪断(取最靠后的那条,别剪到开头去)
  const start = s[0]!;
  let base = t;
  if (t.length === 1) {
    if (px(start, t[0]!) <= snap) base = [];
  } else {
    // 同样只看尾巴那一小截:窄长的一片,起笔挨着对面那条边不算接头
    for (let i = t.length - 2, run = 0; i >= 0 && run <= snap * 4; run += px(t[i]!, t[i + 1]!), i--) {
      const n = nearOnSegment(start, t[i]!, t[i + 1]!);
      if (n.d <= snap && run + px(t[i]!, t[i + 1]!) - n.along <= snap * 4) {
        base = t.slice(0, i + 1);
        break;
      }
    }
  }

  // 收口:落笔回到了开头附近 → 绕完一圈。把开头多出来的那截剪掉
  const end = s[s.length - 1]!;
  let closed = false;
  let head = 0;
  if (base.length >= 1 && px(end, base[0]!) <= snap) {
    closed = true;
    s.pop();
  } else if (px(end, start) > snap * 2) {
    // 落笔离起笔太近的不算:那是在接头附近来回描,不是绕回了开头。
    // 也只看开头那一小截 —— 窄长的一片两条边本来就挨得近,挨着中间哪条边落笔不是收口
    for (let j = 0, run = 0; j + 1 < base.length && run <= snap * 4; run += px(base[j]!, base[j + 1]!), j++) {
      const n = nearOnSegment(end, base[j]!, base[j + 1]!);
      if (n.d <= snap && run + n.along <= snap * 4) {
        closed = true;
        head = j + 1;
        break;
      }
    }
  }
  const path = [...base.slice(head), ...s];
  return { path, closed: closed && path.length >= 3, ignored: false };
}
